import { skipToken, useMutation, useQuery } from "@tanstack/react-query";
import { AlertCircle, Loader2, LucideFlaskConical } from "lucide-react";
import type { EarlyAccessFeature, EarlyAccessFeatureStage } from "posthog-js";
import { useActiveFeatureFlags, usePostHog } from "posthog-js/react";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { useIsKultifyAdmin } from "@/lib/useIsKutifyAdmin";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import {
	Item,
	ItemActions,
	ItemContent,
	ItemDescription,
	ItemHeader,
	ItemTitle,
} from "@/components/ui/item";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { reportError } from "@/lib/sentry";
import { Alert } from "../ui/alert";
import { Empty, EmptyDescription, EmptyMedia, EmptyTitle } from "../ui/empty";
import { Typography } from "../ui/typography";

interface ExperimentsDialogsProps {
	title?: string;
	description?: string;
	children?: React.ReactElement;
	open?: boolean;
	onOpenChange?: (open: boolean) => void;
}

type ActiveEarlyAccessFeature = EarlyAccessFeature & {
	flagKey: string;
	id: string;
};

export function ExperimentsDialogs({
	children,
	title = "Feature Flags & Beta Programs",
	description = "Manage your feature flags and opt-in to beta programs",
	open,
	onOpenChange,
}: ExperimentsDialogsProps) {
	const posthog = usePostHog();
	const activeFlags = useActiveFeatureFlags();
	const isKultifyAdmin = useIsKultifyAdmin();
	const [updatingFlags, setUpdatingFlags] = useState<Set<string>>(new Set());
	const stages: EarlyAccessFeatureStage[] = isKultifyAdmin
		? ["alpha", "beta"]
		: ["beta"];

	// Fetch early access features using TanStack Query
	const {
		data: earlyAccessFeatures = [],
		isLoading: loading,
		error,
	} = useQuery({
		queryKey: ["early-access-features", stages],
		queryFn: posthog
			? async () => {
					const allFeatures = await new Promise<
						(EarlyAccessFeature & { id: string })[]
					>((resolve) => {
						posthog.getEarlyAccessFeatures((features) => {
							resolve(features as (EarlyAccessFeature & { id: string })[]);
						}, true, stages);
					});
					const activeFeatures = allFeatures.filter(
						(feature) => feature.flagKey,
					) as ActiveEarlyAccessFeature[];
					return activeFeatures;
				}
			: skipToken,
		enabled: !!posthog,
	});

	// Toggle feature flag enrollment mutation
	const toggleFeatureFlagMutation = useMutation({
		mutationFn: async ({
			feature,
			isEnrolled,
		}: {
			feature: ActiveEarlyAccessFeature;
			isEnrolled: boolean;
		}) => {
			if (!posthog) throw new Error("PostHog not available");

			// Update PostHog enrollment
			posthog.updateEarlyAccessFeatureEnrollment(feature.flagKey, isEnrolled);

			// Track the toggle event
			posthog.capture("feature_flag_toggled", {
				feature_name: feature.name,
				feature_key: feature.flagKey,
				new_state: isEnrolled,
				action: isEnrolled ? "opted_in" : "opted_out",
			});

			return { feature, newEnrollmentState: isEnrolled, key: feature.flagKey };
		},
		onMutate: ({ feature }) => {
			setUpdatingFlags((prev) => new Set(prev).add(feature.id));
		},
		onError: (error) => {
			reportError(error);
		},
		onSettled: (_, __, { feature }) => {
			setUpdatingFlags((prev) => {
				const newSet = new Set(prev);
				newSet.delete(feature.id);
				return newSet;
			});
		},
	});

	const errorMessage = error
		? "Failed to load beta features. Please try again later."
		: null;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogTrigger render={children} />
			<DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						<LucideFlaskConical className="size-4" />
						{title}
					</DialogTitle>
					{description && <DialogDescription>{description}</DialogDescription>}
				</DialogHeader>

				<div className="space-y-6 mt-4">
					{loading ? (
						<div className="space-y-4">
							{[...Array(3)].map((_, i) => (
								<Item key={i}>
									<ItemHeader>
										<Skeleton className="h-5 w-3/4" />
										<Skeleton className="h-4 w-full" />
									</ItemHeader>
									<ItemContent>
										<Skeleton className="h-6 w-12" />
									</ItemContent>
								</Item>
							))}
						</div>
					) : errorMessage ? (
						<Alert variant={"destructive"}>
							<AlertCircle />
							{errorMessage}
						</Alert>
					) : (
						<>
							{/* Active Features */}
							{earlyAccessFeatures.length > 0 && (
								<div className="space-y-3">
									<div className="flex items-center gap-2">
										<Typography variant={"md"} weight={"semibold"}>
											Available Features
										</Typography>
										<Badge variant="outline">
											{earlyAccessFeatures.length}
										</Badge>
									</div>
									<div className="space-y-2">
										{earlyAccessFeatures.map((feature) => (
											<Item key={feature.id} variant="outline">
												<ItemHeader>
													<ItemContent>
														<ItemTitle>
																{feature.name}
																<Badge
																	variant="outline"
																	className="ml-2 capitalize"
																>
																	{feature.stage}
																</Badge>
															</ItemTitle>
														{feature.description && (
															<ItemDescription>
																{feature.description}
															</ItemDescription>
														)}
													</ItemContent>
													<ItemActions>
														{updatingFlags.has(feature.id) ? (
															<Loader2 className="h-4 w-4 animate-spin" />
														) : (
															<Switch
																checked={activeFlags.includes(feature.flagKey)}
																onCheckedChange={(checked) =>
																	toggleFeatureFlagMutation.mutate({
																		feature,
																		isEnrolled: checked,
																	})
																}
																disabled={updatingFlags.has(feature.flagKey)}
															/>
														)}
													</ItemActions>
												</ItemHeader>
											</Item>
										))}
									</div>
								</div>
							)}

							{/* No features available */}
							{earlyAccessFeatures.length === 0 && (
								<Empty>
									<EmptyMedia variant={"icon"}>
										<LucideFlaskConical />
									</EmptyMedia>
									<EmptyTitle>No Beta Features Available</EmptyTitle>
									<EmptyDescription>
										There are currently no beta features available for early
										access. Check back later for new features and improvements.
									</EmptyDescription>
								</Empty>
							)}
						</>
					)}
				</div>
			</DialogContent>
		</Dialog>
	);
}
